import React, { useEffect, useMemo, useState } from 'react';
import Datatable from '../../components/Datatable';
import FormDialog, { Text } from '../../components/Datatable/FormDialog';
import { Alert, Box, Button, Chip, Divider, FormControl, IconButton, Snackbar, Stack, TextField, Tooltip, Typography, setRef } from '@mui/material';
import { create, get_one, update, delete_one } from '../../services/api';
import EditIcon from '@mui/icons-material/Edit';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import { useFormError } from '../../components/Datatable/FormErrorProvider';
import { RemoveCircleOutline } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { useAuth } from '../../components/AuthProvider';
import { grey } from '@mui/material/colors';
import Swal from 'sweetalert2';

export default function IPPool() {
    const notify = useNotify()
    const [formState, setFormState] = useState({
        open: false,
        id: null,
    })
    const auth = useAuth()
    const [refresh, setRefresh] = useState(false)
    React.useEffect(() => {
        document.title = "IPV7 Tools - Radius - Pools de IP"
    }, [])
    return (
        <>
            <FormItem
                open={formState.open}
                id={formState.id}
                handlerClose={() => setFormState({ open: false, id: null })}
                handlerSubmit={() => setRefresh(true)}
                btnSubmit={formState.id ? 'Atualizar' : 'Salvar'}
                btnClose={'Fechar'}
            ></FormItem>
            <Datatable
                refresh={refresh}
                handlerRefresh={() => setRefresh(!refresh)}
                title='Pools de IP'
                endpoint='/groupip'
                options={{
                    customToolbar: () =>
                        <>
                            <Tooltip title="Remover todos os filtros">
                                <IconButton onClick={() => { localStorage.removeItem('alarms'); location.reload() }} >
                                    <RemoveCircleOutline />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Adicionar">
                                <IconButton
                                    disabled={!auth.hasPerm("group.create")}
                                    onClick={() => setFormState({ open: true, id: null })}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    ,
                    customToolbarSelect: (selectedRows, displayData, setSelectedRows) => ""
                }
                }
                columns={[
                    {
                        name: 'id',
                        label: 'ID',
                        options: {
                            display: false
                        }
                    },
                    {
                        name: 'description',
                        label: 'Nome',
                    },
                    {
                        name: 'ips',
                        label: 'IP Pools',
                        options: {
                            customBodyRender: (value) => value?.split(",").map((ip, index) => <Chip variant="outlined" sx={{ m: .5 }} key={index} label={ip} />)
                        },
                    },
                    {
                        name: 'actions',
                        label: 'Ações',
                        options: {
                            filter: false,
                            sort: false,
                            customBodyRender: (value, tableMeta, updateValue) => {
                                return (
                                    <>
                                        <Tooltip title="Editar">
                                            <IconButton
                                                disabled={!auth.hasPerm("group.update")}
                                                onClick={() => setFormState({ open: true, id: tableMeta.rowData[0] })}
                                            >
                                                <EditIcon />

                                            </IconButton>
                                        </Tooltip>

                                    </>
                                )
                            }
                        }
                    }
                ]}

            />
        </>
    )
}




// ---------------------------------------------------------------------------
// Helpers de validação/cálculo de IP
// ---------------------------------------------------------------------------

const OCTET = '(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9][0-9]?|0)';
const MASK = '([1-2]?[0-9]|3[0-2])';
const IP_RE = new RegExp(`^${OCTET}\\.${OCTET}\\.${OCTET}\\.${OCTET}$`);
const CIDR_RE = new RegExp(`^${OCTET}\\.${OCTET}\\.${OCTET}\\.${OCTET}\\/${MASK}$`);

function isValidIP(s) { return IP_RE.test(s) || CIDR_RE.test(s); }

// Conta hosts cobertos por um IP/CIDR. Single IP = 1.
function ipCount(s) {
    const m = s.match(/^.+\/(\d+)$/);
    if (!m) return 1;
    const bits = parseInt(m[1], 10);
    if (isNaN(bits) || bits < 0 || bits > 32) return 1;
    return Math.pow(2, 32 - bits);
}

// Parseia texto livre (espaços/vírgulas/quebras de linha) → lista única e válida.
function parseIPs(text) {
    if (!text) return [];
    const parts = String(text).split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    const valid = [];
    const invalid = [];
    const seen = new Set();
    for (const p of parts) {
        if (isValidIP(p)) {
            if (!seen.has(p)) {
                seen.add(p);
                valid.push(p);
            }
        } else {
            invalid.push(p);
        }
    }
    return { valid, invalid };
}

// Converte CSV string (formato do banco) → array.
function ipsFromCsv(csv) {
    if (!csv) return [];
    return csv.split(',').map((s) => s.trim()).filter(Boolean);
}


function FormItem({ id, open, handlerClose, handlerSubmit, btnClose = 'Fechar', btnSubmit = 'Salvar' }) {
    const notify = useNotify();
    const error = useFormError();
    const auth = useAuth();
    const [isLoaded, setIsLoaded] = useState(true);

    const [description, setDescription] = useState('');
    const [ipList, setIpList] = useState([]);                    // array de strings ['10.0.0.1', '192.168.0.0/24', ...]
    const [singleIp, setSingleIp] = useState('');                // input do "+ Adicionar"
    const [bulkText, setBulkText] = useState('');                // textarea do bulk
    const [showBulk, setShowBulk] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDescription('');
        setIpList([]);
        setSingleIp('');
        setBulkText('');
        setShowBulk(false);
        error.setErrors([]);
        if (id) {
            setIsLoaded(false);
            get_one('/groupip', id).then((res) => {
                const data = res.data.data || {};
                setDescription(data.description || '');
                setIpList(ipsFromCsv(data.ips));
                setIsLoaded(true);
            }).catch((e) => {
                console.log(e);
                setIsLoaded(true);
            });
        }
    }, [id, open]);

    const totalHosts = useMemo(
        () => ipList.reduce((acc, ip) => acc + ipCount(ip), 0),
        [ipList]
    );

    const singleIsValid = isValidIP(singleIp);
    const singleIsDup = ipList.includes(singleIp);

    const addSingle = () => {
        if (!singleIsValid || singleIsDup) return;
        setIpList([...ipList, singleIp].sort());
        setSingleIp('');
    };

    const removeIp = (ip) => {
        setIpList(ipList.filter((v) => v !== ip));
    };

    const applyBulk = () => {
        const { valid, invalid } = parseIPs(bulkText);
        const novos = valid.filter((v) => !ipList.includes(v));
        if (novos.length === 0 && invalid.length === 0) {
            notify.setNotify({ open: true, message: 'Nenhum IP novo no texto colado.', severity: 'info' });
            return;
        }
        setIpList([...ipList, ...novos].sort());
        setBulkText('');
        setShowBulk(false);
        const msgs = [];
        if (novos.length > 0) msgs.push(`${novos.length} IP(s) adicionados`);
        if (valid.length > novos.length) msgs.push(`${valid.length - novos.length} duplicado(s) ignorados`);
        if (invalid.length > 0) msgs.push(`${invalid.length} entrada(s) inválida(s) ignoradas`);
        notify.setNotify({ open: true, message: msgs.join(' · '), severity: invalid.length > 0 ? 'warning' : 'success' });
    };

    const pasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text && text.trim()) {
                setBulkText((prev) => prev ? prev + '\n' + text : text);
                setShowBulk(true);
            }
        } catch {
            notify.setNotify({ open: true, message: 'Não foi possível ler a área de transferência. Cole manualmente (Ctrl+V).', severity: 'warning' });
        }
    };

    const wrapperSubmit = () => {
        error.setErrors([]);
        if (!description.trim()) {
            error.setErrors([{ field: 'description', message: 'Nome do pool é obrigatório' }]);
            return;
        }
        const payload = { description, ips: ipList.join(',') };
        const onResp = (res, label) => {
            if (res.data?.errors) { error.setErrors(res.data.errors); return; }
            if (res.data?.error) { notify.setNotify({ open: true, message: res.data.error, severity: 'error' }); return; }
            handlerClose();
            notify.setNotify({ open: true, message: `${label} com sucesso`, severity: 'success' });
            error.setErrors([]);
            handlerSubmit();
        };
        const onErr = (e) => notify.setNotify({ open: true, message: e.message, severity: 'error' });

        if (id) {
            update('/groupip', id, payload).then((r) => onResp(r, 'Pool atualizado')).catch(onErr);
        } else {
            create('/groupip/create', payload).then((r) => onResp(r, 'Pool criado')).catch(onErr);
        }
    };

    return (
        <Box component="form" onSubmit={wrapperSubmit} sx={{ m: 2 }} display="flex" flexWrap="wrap">
            <FormDialog
                isLoaded={isLoaded}
                maxWidth="md"
                title={
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" component="div">
                            {id ? 'Editar' : 'Adicionar'} Pool de IP
                        </Typography>
                        <IconButton
                            disabled={!auth.hasPerm('group.delete')}
                            sx={{ display: id ? 'block' : 'none' }}
                            onClick={() => {
                                Swal.fire({
                                    title: 'Excluir pool?',
                                    text: 'Esta ação remove o pool. Acessos vinculados podem ficar sem IPs.',
                                    icon: 'warning',
                                    showCancelButton: true,
                                    confirmButtonText: 'Sim, excluir',
                                    cancelButtonText: 'Cancelar',
                                    confirmButtonColor: '#e01b29',
                                }).then((result) => {
                                    if (!result.isConfirmed) return;
                                    delete_one('/groupip', id).then(() => {
                                        notify.setNotify({ open: true, message: 'Pool deletado com sucesso.', severity: 'success' });
                                        handlerClose();
                                        handlerSubmit();
                                    });
                                });
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                }
                open={open}
                handlerSubmit={wrapperSubmit}
                handlerClose={handlerClose}
                btnSubmit={btnSubmit}
                btnClose={btnClose}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        sx={{ mt: 1 }}
                        error={error.verify('description')}
                        helperText={error.msgError('description')}
                        label='Nome do pool'
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        autoFocus
                    />

                    {/* Header com contadores */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            IPs e ranges
                        </Typography>
                        <Chip
                            size="small"
                            label={`${ipList.length} entrada(s)`}
                            color={ipList.length > 0 ? 'primary' : 'default'}
                            variant="outlined"
                        />
                        <Chip
                            size="small"
                            label={`~${totalHosts.toLocaleString('pt-BR')} host(s) cobertos`}
                            variant="outlined"
                        />
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            size="small"
                            startIcon={<ContentPasteIcon />}
                            onClick={() => setShowBulk(!showBulk)}
                            variant={showBulk ? 'contained' : 'outlined'}
                        >
                            {showBulk ? 'Esconder colar em massa' : 'Colar em massa'}
                        </Button>
                    </Box>

                    {/* Bulk paste textarea */}
                    {showBulk && (
                        <Box sx={{ p: 2, border: '1px dashed #ccc', borderRadius: 1, bgcolor: '#fafafa' }}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                                Cole IPs ou CIDRs separados por espaço, vírgula ou linha:
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                placeholder="10.0.0.1&#10;192.168.0.0/24&#10;172.16.0.0/16, 10.10.0.0/16"
                                value={bulkText}
                                onChange={(e) => setBulkText(e.target.value)}
                                sx={{ mb: 1, fontFamily: 'monospace' }}
                            />
                            <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={pasteFromClipboard} startIcon={<ContentPasteIcon />}>
                                    Colar da área de transferência
                                </Button>
                                <Box sx={{ flexGrow: 1 }} />
                                <Button size="small" onClick={() => setBulkText('')}>Limpar</Button>
                                <Button size="small" variant="contained" onClick={applyBulk} disabled={!bulkText.trim()}>
                                    Adicionar todos
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {/* Single IP add */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                            fullWidth
                            label="IP ou IP/máscara (CIDR)"
                            placeholder="10.0.0.1 ou 192.168.0.0/24"
                            value={singleIp}
                            onChange={(e) => setSingleIp(e.target.value.trim())}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addSingle();
                                }
                            }}
                            error={singleIp.length > 0 && (!singleIsValid || singleIsDup)}
                            helperText={
                                singleIp.length === 0 ? ' '
                                    : !singleIsValid ? 'Formato inválido (ex.: 10.0.0.1 ou 192.168.0.0/24)'
                                    : singleIsDup ? 'IP/range já adicionado'
                                    : 'Pressione Enter ou clique em + para adicionar'
                            }
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!singleIsValid || singleIsDup}
                            sx={{ height: 56, minWidth: 56 }}
                            onClick={addSingle}
                        >
                            <AddIcon />
                        </Button>
                    </Box>

                    <Divider />

                    {/* Lista de IPs */}
                    {ipList.length === 0 ? (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            Nenhum IP adicionado ainda. Use o campo acima ou "Colar em massa".
                        </Alert>
                    ) : (
                        <Box sx={{ maxHeight: 280, overflow: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {ipList.map((ip) => {
                                const hosts = ipCount(ip);
                                const isCidr = ip.includes('/');
                                return (
                                    <Tooltip key={ip} title={isCidr ? `${hosts.toLocaleString('pt-BR')} host(s) neste range` : 'Single IP'}>
                                        <Chip
                                            label={ip}
                                            onDelete={() => removeIp(ip)}
                                            sx={{ fontFamily: 'monospace' }}
                                            color={isCidr ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                    </Tooltip>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </FormDialog>
        </Box>
    );
}


